'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'

interface PriceModeToggleProps {
  /** Compact variant for inline use next to sliders */
  variant?: 'default' | 'compact'
  className?: string
}

export function PriceModeToggle({ variant = 'default', className }: PriceModeToggleProps) {
  const { t } = useI18n()
  const { filters, updateFilter } = useSearchStore()
  const useTotalPrice = filters.use_total_price !== false // default true
  const [showTooltip, setShowTooltip] = React.useState(false)
  const iconRef = React.useRef<HTMLButtonElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)
  const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number } | null>(null)

  const handleToggle = (total: boolean) => {
    updateFilter('use_total_price', total)
  }

  // Position the tooltip above the icon, anchored right
  React.useEffect(() => {
    if (!showTooltip || !iconRef.current) return
    const rect = iconRef.current.getBoundingClientRect()
    const tooltipWidth = 256 // w-64 = 16rem = 256px
    setTooltipPos({
      top: rect.top - 8,
      left: Math.max(8, rect.right - tooltipWidth),
    })
  }, [showTooltip])

  // Close tooltip when clicking outside
  React.useEffect(() => {
    if (!showTooltip) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        tooltipRef.current && !tooltipRef.current.contains(target) &&
        iconRef.current && !iconRef.current.contains(target)
      ) {
        setShowTooltip(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showTooltip])

  // Close on scroll (the modal content scrolls)
  React.useEffect(() => {
    if (!showTooltip) return
    const handleScroll = () => setShowTooltip(false)
    document.addEventListener('scroll', handleScroll, true)
    return () => document.removeEventListener('scroll', handleScroll, true)
  }, [showTooltip])

  const isCompact = variant === 'compact'

  return (
    <div className={cn('inline-flex items-center gap-1.5', className)}>
      {/* Segmented toggle */}
      <div
        className={cn(
          'inline-flex rounded-full border border-border bg-muted/50 p-0.5',
          isCompact && 'text-xs'
        )}
      >
        <button
          type="button"
          onClick={() => handleToggle(false)}
          className={cn(
            'rounded-full font-medium transition-all',
            isCompact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
            !useTotalPrice
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {t('listingCard.rent', 'Rent')}
        </button>
        <button
          type="button"
          onClick={() => handleToggle(true)}
          className={cn(
            'rounded-full font-medium transition-all',
            isCompact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
            useTotalPrice
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {t('listingCard.estTotal', 'Est. total')}
        </button>
      </div>

      {/* Info icon */}
      <button
        ref={iconRef}
        type="button"
        onClick={() => setShowTooltip(!showTooltip)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="text-muted-foreground hover:text-foreground transition-colors"
        aria-label={t('listingCard.estTotal', 'What is estimated total?')}
      >
        <Info className={cn(isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
      </button>

      {/* Tooltip — rendered via portal to escape overflow-hidden ancestors */}
      {showTooltip && tooltipPos && createPortal(
        <div
          ref={tooltipRef}
          className={cn(
            'fixed z-[200] w-64 p-3 rounded-lg shadow-lg',
            'bg-popover text-popover-foreground border border-border',
            'text-xs leading-relaxed'
          )}
          style={{ top: tooltipPos.top, left: tooltipPos.left, transform: 'translateY(-100%)' }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {t(
            'search.estTotalTooltip',
            'Estimated total includes rent, utilities, service charges, parking, and internet — extracted by AI from listing descriptions. Listings without this data use base rent.'
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
